import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, Flex, LabelledCheckbox, Text } from '@modusign/design-system';
import { useGetUserInvitation } from '@modusign/openapi-client-user';

import useResponsiveValue from '@app/hooks/useResponsiveValue';

interface InvitationInfoProps {
  isChecked: boolean;
  handleClickCheckBox: () => void;
}

export default function InvitationInfo({ handleClickCheckBox, isChecked }: InvitationInfoProps) {
  const userInvitation = useGetUserInvitation();
  const isMobileViewport = useResponsiveValue([true, false, false]);

  if (!userInvitation.data) {
    return null;
  }

  const { groups, automaticSharingTargets, role } = userInvitation.data;

  const inviteeAutomaticSharingTargetNames = automaticSharingTargets.map(
    (t) => t.member?.name ?? t.group?.name
  );
  const inviteeWorkspaceGroupNames = groups.map((g) => g.name);
  const inviteeWorkspaceRoleName: string | undefined = role?.name;
  const hasAnyOfGrantedThings = Boolean(
    inviteeWorkspaceGroupNames.length ||
      inviteeWorkspaceRoleName ||
      inviteeAutomaticSharingTargetNames.length
  );

  return (
    <>
      <Text as="p" fontSize="lg" fontWeight="medium" mb={3}>
        공용 워크스페이스에선 어떤 점이 편리해지나요?
      </Text>
      <Flex flexDirection="column" gap={1} mb={7}>
        <Flex>
          <Box as={FontAwesomeIcon} color="iconNeutralSlightlyDark" icon={faCheck} mt={1} />
          <Text ml={1}>멤버들과 함께 문서의 진행 상태를 확인 및 관리할 수 있습니다.</Text>
        </Flex>
        <Flex>
          <Box as={FontAwesomeIcon} color="iconNeutralSlightlyDark" icon={faCheck} mt={1} />
          <Text ml={1}>템플릿을 공유하고, 공유받은 템플릿을 함께 사용할 수 있습니다.</Text>
        </Flex>
      </Flex>

      {!isMobileViewport && hasAnyOfGrantedThings && (
        <Box
          bg="boxBackgroundNeutralBase"
          borderColor="boxBorderNeutralBase"
          borderStyle="solid"
          borderWidth="1px"
          mb={5}
        >
          <Flex flexDirection="column" gap={4} p={6}>
            {!!inviteeWorkspaceGroupNames.length && (
              <InvitationInfoLabel label="내 그룹">
                {inviteeWorkspaceGroupNames.join(' · ')}
              </InvitationInfoLabel>
            )}

            {inviteeWorkspaceRoleName && (
              <InvitationInfoLabel label="내 역할">{inviteeWorkspaceRoleName}</InvitationInfoLabel>
            )}

            {!!inviteeAutomaticSharingTargetNames.length && (
              <InvitationInfoLabel label="내 자동 공유">
                {inviteeAutomaticSharingTargetNames.join(' · ')}
              </InvitationInfoLabel>
            )}
          </Flex>
        </Box>
      )}

      {isMobileViewport ? (
        <Flex>
          <Box>
            <Checkbox checked={isChecked} onClick={handleClickCheckBox} />
          </Box>
          <Box flex={1} ml={2}>
            <Text fontWeight="medium">
              초대 수락 시 개인 워크스페이스 내 전송 예약된 문서는 자동 취소됩니다.
            </Text>
          </Box>
        </Flex>
      ) : (
        <LabelledCheckbox checked={isChecked} variant="medium" onClick={handleClickCheckBox}>
          초대 수락 시 개인 워크스페이스 내 전송 예약된 문서는 자동 취소됩니다.
        </LabelledCheckbox>
      )}
    </>
  );
}

function InvitationInfoLabel({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <Flex alignItems="center" flex={1} gap={4}>
      <Text fontSize="sm" sx={{ flex: '0 0 70px' }}>
        {label}
      </Text>
      <Text fontWeight="medium" sx={{ flex: '1 0 332px' }}>
        {children}
      </Text>
    </Flex>
  );
}
